/**
 * DeleteGoalDialog - confirmation dialog for deleting a goal tree node.
 * Warns when the node has children (they cascade or block, depending on backend policy).
 */

import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import type { GoalNode } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { useDeleteGoal } from '../../hooks/useGoals';
import { extractApiError } from '../../utils/apiError';
import { LEVEL_LABELS } from './statusMeta';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.warning.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    margin-top: 2px;
  }
`;

const FormError = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

interface DeleteGoalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  goal: GoalNode | null;
  /** Overlay z-index — raise this when opened on top of another high-z overlay (e.g. a drawer). */
  zIndex?: number;
}

export function DeleteGoalDialog({
  isOpen,
  onClose,
  clientId,
  goal,
  zIndex,
}: DeleteGoalDialogProps) {
  const deleteGoal = useDeleteGoal(clientId);

  const handleClose = () => {
    deleteGoal.reset();
    onClose();
  };

  const handleConfirm = () => {
    if (!goal) return;
    deleteGoal.mutate(goal.id, { onSuccess: handleClose });
  };

  if (!goal) return null;

  const hasChildren = goal.children.length > 0;
  const errorMessage = deleteGoal.isError
    ? extractApiError(deleteGoal.error, 'Failed to delete this goal. Please try again.')
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Delete ${LEVEL_LABELS[goal.level]}`}
      size="sm"
      zIndex={zIndex}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={deleteGoal.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} isLoading={deleteGoal.isPending}>
            Delete
          </Button>
        </>
      }
    >
      <Body>
        {errorMessage && <FormError>{errorMessage}</FormError>}

        <Message>
          Are you sure you want to delete <strong>{goal.name}</strong>?
        </Message>

        {hasChildren && (
          <WarningBanner>
            <AlertTriangle />
            <span>
              This {LEVEL_LABELS[goal.level].toLowerCase()} has {goal.children.length} child
              {goal.children.length !== 1 ? 'ren' : ''}. Deleting it may remove or orphan everything
              nested underneath.
            </span>
          </WarningBanner>
        )}
      </Body>
    </Modal>
  );
}

export default DeleteGoalDialog;
